import { create } from 'lodash';

const en = {
  mte_management: 'MTE Management',
  qr_code_reader: {
    no_front_camera: "You don't seem to have a front camera on your device",
    no_rear_camera: "You don't seem to have a rear camera on your device",
    change_camera: "Change Camera",
    error_permission: "ERROR: you need to grant camera access permission",
    error_no_camera: "ERROR: no camera on this device",
    error_secure_context: "ERROR: secure context required (HTTPS, localhost)",
    error_in_use: "ERROR: is the camera already in use?",
    error_unsuitable: "ERROR: installed cameras are not suitable",
    error_stream_api: "ERROR: Stream API is not supported in this browser",
    error_secure_context_detail: "ERROR: Camera access is only permitted in secure context. Use HTTPS or localhost rather than HTTP.",
    error_generic: "ERROR: Camera error ({errorName})",
    invalid_qr_code: "Invalid QR Code: Not a valid Job ID"
  },
  select_user_site: "Select the site which the new assigned user is in",
  select_user_site_placeholder: "Select the user site",
  select_user_reassign_placeholder: "Select a user to reassign the jobs",
  qr_code_viewer: {
    close: "Close",
    error_exporting_traveler: "Error exporting traveler job: {title} - {info}"
  },
  tasks_header: {
    options: "Options",
    progress: "Progress",
    save_offline: "Save offline",
    sync_job: "Sync Job",
    documents: "Documents",
    instrument_log: "Instrument Log",
    copy_from: "Copy From",
    complete_job: "Complete Job",
    reopen_job: "Reopen Job",
    job_reopened_successfully: "Job reopened successfully",
    failed_reopen_job: "Failed to reopen the Job",
    complete_job_confirmation: "Some tasks are not yet signed off. Continue to complete them all?",
    job_completed_notification: "Job completed successfully. Notification sent to {client}"
  },
  documents: "Documents",
  take_photo: "Take photo",
  saved: "Saved",
  rat_audit_ok: "All parts in the RAT table have been quality control checked.",
  rat_audit_fail: "Some parts in the RAT table have not been quality control checked.",
  upload_rat: "Upload RAT",
  forbidden_page: {
    message: 'Sorry, you are not allowed to see this page.',
  },

  page_404: {
    message: '(404) Sorry, this page does not exist.',
    click: 'Click',
    here: 'here',
    to_return: 'to return to the home page.',
  },

  login_page: {
    digital: 'Digital',
    inspection_jacket: 'Inspection Jacket',
    logging_in: 'Logging in...',
  },

  dashboard_page: {
    welcome: 'Welcome',
    intro: 'End to end solution for digital inspection.',
    scan_project_code: 'Scan Project Code',
    new_version_available: 'New version available!',
    websocket_status: 'WebSocket Status',
    dij_version: 'Digital Inspection Jacket {version}',
    job_checked_out_offline: 'This job is checked out for offline use.',
  },

  header: {
    app_name: 'Digital Inspection Jacket',
  },

  task_page: {
    create_new_task: 'Create a New Task',
    back_to_product_list:
      'After you are done here, please, go back to Template List so that others can work on this template',
    back_to_work_order_list:
      'After you are done here, please, go back to Project List so that others can work on this order',
    drop_file_reference_image: 'Drop your file here or click to upload Reference Image',
    reference_image: 'Reference Image',
    signed_off_by: 'Signed Off by {name}',
    task_not_available_offline: 'Task is not available offline',
    need_help_chat: 'Need help? Click here to chat!',
    loading_user_roles: 'Loading user roles...',
    loading_job_details: 'Loading job details...',
    checking_job_status: 'Checking job status...',
    loading_job_tasks: 'Loading job tasks...',
    preparing_tasks: 'Preparing tasks...',
    deleting_task: 'Deleting task...',
    duplicating_folder: 'Duplicating folder...',
    select_folder_to_move: 'Select folder to move.',
    select_phase_to_move: 'Select phase to move task to.',
    change_phase: 'Change phase',
    delete_folder_confirmation_message: 'Deleting a folder will delete all tasks inside it.',
    delete_confirmation: 'Are you sure you want to delete?',
  },

  site_edit_permission_denied: "You don't have permission to edit sites.",

  create_new_user_page: {
    create_new_user: 'Create new user',
    create_user: 'Create user',
    firstName: 'First Name',
    lastName: 'Last Name',
    gid: 'GID',
    email: 'E-mail',
    password: 'Password',
    role: 'Role',
    all_fields_required: 'Please, fill in all the required the fields.',
    user_failed: 'User creation failed.',
    user_success: 'User was successfully created.',
    password_validation: 'Password requirements: at least 8 characters, a lowercase letter, an uppercase letter, a number, no parts of your username.'
  },

  change_password_page: {
    change_password: 'Change password',
    change_user_password: 'Change user password',
    password_change_success: 'Password was changed successfully',
    secret_question_success: 'Secret question was set successfully',
    password_change_fail: 'Failed to change the password.',
    secret_question_fail: 'Failed to set secret question',
    email: 'E-mail',
    oldPassword: 'Old password',
    password: 'New password',
    confirmNewPassword: 'Confirm new password',
    password_should_match: 'New password and confirmed password must match.',
    password_validation: 'Password requirements: at least 8 characters, a lowercase letter, an uppercase letter, a number, no parts of your email.',
    passwordLowerCase: "Password must contain at least one lowercase character.",
    passwordUpperCase: "Password must contain at least one uppercase character.",
    passwordNumber: "Password must contain at least one digit.",
    passwordMinLength: "Password must be at least 8 characters long.",
    passwordNoUsernameParts: "Password cannot contain any part of the username."
  },

  profile_page: {
    myProfile: 'My Profile',
    account: 'Account',
    preferences: 'Preferences',
    profileUpdated: 'Profile updated successfully',
    updatePreferences: 'Update Preferences',
    updateAccount: 'Update Account',
    my_name: 'My Name',
    username: 'Username',
    email: 'Email',
    location: 'Location',
    phone_number: 'Phone Number',
    units: 'Units',
    inches: 'Inches',
    millimetres: 'Millimetres',
  },

  task_view: {
    cancel: 'Cancel',
    back: 'Back',
    save: 'Save',
    delete: 'Delete',
    deleteConfirmation: 'Are you sure you want to <b>delete</b> task <b>{taskTitle}</b>?',
    deleteTask: 'Delete task',
    manageMte: 'Instrumentation Log',
    repairAssessment: 'Repair Assessment',
    importAccuScan: 'Import AccuScan',
    importLaserscan: 'Import Laser Scan',
    signOff: 'Sign Off',
    orderItem: 'Order Item'
  },

  text_size: 'Text size',
  selection: 'Selection',
  alignedlength: 'Aligned length',
  linearlength: 'Linear length',
  positive_tolerance: '+ Tolerance',
  negative_tolerance: '- Tolerance',

  form_library: 'Form Library',
  product_templates_module: 'Product Templates',
  product_template_list: 'Template List',
  work_order_list: 'Project List',
  update: 'Update',
  order: 'Order',
  select_folder: 'Select Folder',
  move: 'Move',
  add_task: 'Add Task',
  add_folder: 'Add Folder',
  add_field: 'Add Field',
  message_order_drag: 'Now you can order your tasks, just drag them',
  move_folder: 'Move to folder',
  move_selected:'Move Selected',
  select:'Select',
  delete_selected:'Delete Selected',
  



  admin: 'Admin',
  editor: 'Editor',
  publisher: 'Publisher',
  inspector: 'Inspector',
  report: 'Report',
  dashboard: 'Dashboard',
  profile: 'Profile',
  language: 'Language',
  view_profile: 'View Profile',
  manage_fleet_data: 'Manage Fleet Data',
  report_issue: 'Report Issue',
  request_content: 'Request Content',
  contact_us: 'Contact Us',
  forum:'Viva Engage Forum',
  training: 'Training',
  documentation: 'Documentation',
  log_out: 'Log out',

  choose_language: 'Choose your preferred language.',

  create_new_product: 'Create New Template',
  task: 'Task',
  font_size: 'Font Size',
  title: 'Title',
  product_line: 'Product Line',
  original_OEM: 'Original OEM',
  original_oem: 'Original OEM',
  brand: 'Brand',
  frame_type: 'Frame Type',
  model: 'Model',
  choose_files: 'Choose files',
  ocr: 'OCR',
  part_number: 'Part Number',
  document_number: 'Document Number',
  revision: 'Revision',
  cancel: 'Cancel',
  submit: 'Submit',
  delete: 'Delete',
  save: 'Save',
  done: 'Done',
  new_product_success: 'New Product created successfully.',
  new_order_success: 'New Order Created Successfully!',
  new_product_fail_message: 'Error while creating new job! Please try again.',
  new_order_fail_message: 'Error while creating new order! Please try again.',
  select_sites: 'Select Sites',
  product_created_success: 'Product Created Successfully!',
  product_updated_success: 'Product Updated Successfully!',
  template: 'Template',
  configuration: 'Configuration',
  sales_force_opportunity_number: 'Sales Force Opportunity Number',
  select_all_limit_tooltip: 'The select all button will add 15 templates (limit)',
  select_all: 'Select all',
  deselect_all: 'Deselect all',
  select_everything: 'Select Everything',
  deselect_everything: 'Deselect Everything',
  import_limit_message: 'Only 15 templates were imported, the limit was reached.',
  sorting_gmtl: 'Sorting according to GMTL',

  standard_tasks: {
    label: 'Standard Tasks',
    breadcrumb_root: 'Standard Tasks',
    add_button: 'Add Standard Task',
    create_new_standard_task: 'Create New Standard Task',
    release_button: 'Release Standard Task',
    revert_to_draft_button: 'Revert to Draft',
    table: {
      document_number: 'Document Number',
      title: 'Title',
      linked_work_instructions: 'Linked WIs',
      category: 'Category',
      status: 'Status',
      product_line: 'Product Line',
      oem: 'OEM',
      frame_type: 'Frame Type',
      model: 'Model',
      type: 'Type',
      date: 'Date Modified',
    },
    empty_default: 'No standard tasks have been created yet.',
  },

  work_instructions: {
    label: 'Work Instructions',
    release_button: 'Release Work Instruction',
    revert_to_draft_button: 'Revert to Draft',
    delete_confirmation_restorable:
      'Are you sure you want to delete <strong>{name}</strong>? You can restore it from the Recycle Bin.',
    add_button: 'Add Work Instruction',
    loading: 'Loading work instruction...',
    edit_title: 'Edit Work Instruction',
    create_title: 'Create New Work Instruction',
    placeholders: {
      product_line: 'Product Line *',
      category: 'Category *',
      project_phase: 'Project Phase *',
      original_oem: 'Original OEM *',
      frame_type: 'Frame Type *',
      model: 'Model *',
    },
    fields: {
      title: 'Title',
      part_number: 'Part Number',
      document_number: 'Document Number',
      revision: 'Revision',
      sap_reference: 'SAP Reference',
    },
    table: {
      name: 'ID',
      description: 'Title',
      version: 'Version',
      owner: 'Owner',
      status: 'Status',
      product_line: 'Product Line',
      category: 'Category',
      project_phase: 'Project Phase',
      linked_instruction_packages: 'Linked Instruction Packages',
      linked_tasks: 'Linked Tasks',
      linked_templates: 'Linked Templates',
      date: 'Date Modified',
      favorite: 'Favorite',
    },
    empty_search: 'No work instructions match your search.',
    empty_default: 'No work instructions have been created yet.',
    instruction_packages: {
      label: 'Instruction Packages',
      release_button: 'Release Package',
      add_button: 'Add Work Instruction Package',
      link_button: 'Link Work Instruction',
      empty_linked: 'No work instructions are currently linked.',
      not_found: 'Instruction package not found.',
      modal: {
        title: 'Link Work Instruction',
        columns: {
          name: 'ID',
          description: 'Title',
          linked_instruction_packages: 'Linked Instruction Packages',
          linked_tasks: 'Linked Tasks',
          linked_templates: 'Linked Templates',
          open_wi: 'Open WI',
        },
        search_placeholder: 'Search',
        empty_available: 'No available work instructions to link.',
        link_action: 'Link',
      },
    },
  },

  // Tool names inside diagram
  'Selection Tool': 'Selection Tool',
  'Pan Viewport': 'Pan Viewport',
  'Diameter': 'Diameter',
  'Run-Out': 'Run-out',
  'Linear Length': 'Linear Length',
  'Visual Inspection': 'Visual Inspection',
  'Text': 'Text',
  'Generic Measurement': 'Generic Measurement',
  'Arrow': 'Arrow',
  'Line': 'Line',
  'Replace Image': 'Replace Image',

  // Bubble names
  generic: 'Generic Marker',
  generic_name: 'Generic Name',
  runout: 'Runout',
  length: 'Length',
  diameter: 'Diameter',
  arrow: 'Arrow',
  line: 'Line',
  text: 'Text',
  table: 'Table',
  replace: 'Replace Image',
  visualInspection: 'Visual Inspection',
  visual: 'Visual Inspection',
  'position-image': 'Position Image',
  positionImageSingle2or4:
    'Single Component – 2 or 4 Measurement Positions (Length, Thickness, or Runout)',
  positionImageSingle6:
    'Single Component – 6 Measurement Positions (Length, Thickness, or Runout)',
  positionImageSplit6:
    'Split Component/Two Halves – 6 Measurement Positions/3 Per Half (Length or Thickness)',
  positionImageSingleDiameter2:
    'Single Component – 2 Measurement Positions (Diameter)',
  positionImageSingleDiameter4:
    'Single Component – 4 Measurement Positions (Diameter)',
  positionImageSplitDiameter3:
    'Split Component/Two Halves – 3 Measurement Positions (Diameter)',
  positionImageJournalBearing:
    'Journal Bearing – 3 Measurement Positions (Diameter or Runout)',
  'alignedLength': 'Aligned Length',
  'linearLength': 'Linear Length',

  // Bubble properties
  label_numbers_only: 'Label (Numbers only)',
  expected_value: 'Expected Value',
  decimals: 'Decimals',
  units: 'Units',
  inch: 'inch',
  mm: 'mm',
  tolerance_type: 'Type of tolerance',
  tolerance: 'Tolerance',
  multiple_positions: 'Multiple Positions',
  edit_table: 'Edit Table',
  orientation: 'Orientation',
  measurement_name: 'Measurement Name',
  visual_name: 'Visual Name',
  marker_description: 'Description',
  generic_marker_tolerance: 'Tolerance',
  direction: 'Direction',
  degrees: 'Degrees',
  clock: 'Clock',
  rotate_text: 'Rotate Text',
  rubbed: 'Rubbed',
  damaged: 'Damaged',
  pitted: 'Pitted',
  eroded: 'Eroded',
  high: 'High',
  low: 'Low',
  medium: 'Medium',
  acceptable: 'Acceptable',
  all: 'All',
  position: 'Position',
  tir: 'TIR',
  ok: 'Ok',
  in: 'inch',
  iso_name: 'ISO',
  iso_grade: 'ISO Grade',
  RAT: 'RAT',
  none: 'None',

  // Save or discard dialog
  save_or_discard_dialog_content: 'Do you want to save your changes ?',
  discard: 'Discard',
  stay: 'Stay',
  back: 'Back',
  dismiss: 'Dismiss',

  import_drawing: 'Import a drawing',
  work_order_list: 'Arbeitsauftragsliste',

  edit_name: 'Edit name',
  reopen: 'Reopen',

  //Instruction actions
  create_new_item: 'Create a new item',
  choose_one_item: ' Choose one item',
  order_item: 'Order item',
  action: 'Action',
  yes_no: 'Yes/No',
  options: 'Options',
  add_iso: 'Add ISO',
  add_calculation: 'Add Calculation',
  recover: 'Recover',
  loading: 'Loading...',
  clear_filters: 'Clear filters',
  job_downloaded_locally: 'Job downloaded locally',
  job_has_incomplete_data: 'Job has incomplete data',
  job_includes_repair_assessment: 'This job includes repair assessments',
  text: 'Text',
  sap_reference: 'SAP Reference',
  filter: 'Filter',
  current_owner: 'Current Owner',
  current_owner_date: 'Current Owner / Date',

  template_list: {
    retrieving_listings: 'Retrieving listings based on your search...',
    fetched_pages_found: 'Fetched {batchCount} pages so far. Found {size} listings...',
    found_listings: 'Found {length} listings matching the search criteria.',
    failed_to_fetch_listings: 'Failed to fetch listings',
    loading_list: 'Loading {listName}...',
    loading_list_progress: 'Loading {listName}... ({processedCount}/{total})',
    loaded_list: 'Finished loading {listName}',
    loading_job: 'Loading job...',
    next_page: 'Next page',
    previous_page: 'Previous page',
    page_label: 'Page',
    current_page: 'Current page',
    csm_repair_engineer: 'CSM / Repair Engineer',
    warning: "Warning",
    repair_select_assistant: "Repair Select Assistant",
    how_can_i_help: "How can I help?",
    no_results_clear_filters: "Search returned no results. Clear filters to get data.",
    checked_out_by: "Checked out by {user}",
    completeness: "Completeness: {percentage}%",
    delete_confirmation_title: "DELETE",
    delete_confirmation_message: "Please confirm deletion of {title}",
    delete_confirm_button: "Delete",
    max_favorites_warning: "You can only add up to 140 favourite jobs.",
    job_needs_sync: "Job has data that require synchronization",
    filter_favorites: 'Filter by favorites',
    filter_favorites_active: 'Clear favorites filter',
  },

  languages: {
    en: 'English',
    de: 'German',
    pt: 'Portuguese',
    it: 'Italian',
  },

  copy_from_page: {
    copy_from: "Copy From",
    copying_tasks_to: "Copying tasks to \"{title}\"",
    publisher_tasks: "Publisher Tasks",
    editor_tasks: "Editor Tasks",
    search: "Search...",
    select_all_limit_tooltip: "The select all button will add 30 tasks (limit)",
    select_site: "Select site",
    doc_no: "Doc no.",
    sorting_gmtl_tooltip: "Sorting according to GMTL",
    work_order: "Work Order",
    duplicated_task: "Duplicated Task",
    task_already_selected: "Task '{name}' is already selected",
    limit_reached: "Only {limit} tasks were imported, the limit was reached. The last template to get tasks was: {title}"
  },

  table: 'Table',
  import_csv: 'Import CSV',

  action_requested: 'Action Requested',
  picture_taken_is_mandatory: 'Picture taken is mandatory.',
  picture_uploaded_successfully: '{image} Uploaded Successfully',
  picture_required: 'Picture required.',
  your_question: 'Your question',
  question: 'Question',
  options: 'Option',
  add_an_option: 'Add an option',
  multiple_choice: 'Multiple Choice',
  single_choice: 'Single Choice',
  text_or_number: 'Text or Number',

  number_of_rows: 'Number of rows',
  number_of_columns: 'Number of columns',
  edit_rows: 'Edit rows',
  edit_columns: 'Edit columns',
  only_numbers_and_alphabets: 'Only numbers and alphabets upto 16 characters are allowed inside a cell',
  table_headings_not_empty: 'Table headings cannot be empty',
  upload_diagram: 'Upload diagram',

  yes: 'Yes',
  no: 'No',


  product_template_list: 'Template List',
  product_line: 'Product Line',
  work_order_list: 'Project List',
  favorite_list: 'Favorite List',
  search: 'Search',
  search_ellipsis: 'Search...',
  add_template: 'Add Form',
  add_template_product_templates: 'Add Template',
  add_project: 'Add Project',
  title: 'Title',
  original_OEM: 'Original OEM',
  frame_type: 'Frame Type',
  model: 'Model',
  part_number: 'Part Number',
  location: 'Location',
  document_number: 'Document Number',
  revision: 'Revision',
  created_by: 'Created By',
  date: 'Date',
  add: 'Add',
  edit: 'Edit',
  delete: 'Delete',
  duplicate: 'Duplicate',
  copyfrom: 'CopyFrom',
  nothing_here: 'Nothing here',

  project_number: 'Project Number',
  customer: 'Customer',
  work_order_number: 'Work Order Number',
  serial_number: 'Serial Number',
  sales_force_number: 'Sales Force Number',
  add_order: 'Create New Project',
  edit_order: 'Edit Order',
  original_oem_number: 'Original OEM Number',
  freefinding: 'Free Finding',
  csm_label:'Client Service Manager (CSM) / Project Manager',
  repair_engineer_label:'Repair Engineer / Project Engineer',

  part_level: 'Part Level',
  ignore: 'Ignore',
  component: 'Component',
  crop_image: 'Crop Image',
  create_rat: 'Create RAT',
  major: 'Major',
  minor: 'Minor',
  select_page: 'Select Page {currentPage} of {totalPages}',

  new_product_success_message: 'New Product Created Successfully!',
  new_product_fail_message: 'Error while creating new job! Please try again.',


  task_count: 'Task Count',
  selection_from_list: 'If desired make a selection from the list',
  select_tasks: 'Select Tasks',
  select_products: 'Select Products (Optional)',

  select_all: 'Select All',
  deselect_all: 'Deselect All',
  select_site: 'Select Site',
  selected_products: 'Selected Products',
  review: 'Review',
  sites: 'Secondary Sites',
  primary: 'Primary',
  secondary: 'Secondary',
  products: 'Products',
  tasks: 'Tasks',
  next: 'Next',

  sign_off: 'Sign Off',
  sign_off_questions_completed: '{completed}/{total} completed.',
  sign_off_diagram_completed: 'Diagram {percent}% completed.',
  sign_off_comments_label: 'Please enter your comments here...',
  number_of_measurement_points: 'Number of measurement points',
  stage: 'Stage',
  number_of_stages: 'Number of stages',
  add_job_with_table_message: 'Sorry, you cannot add new jobs since you selected a job with a task table inside it.',
  configure_table_order: 'Configure Table',
  no_options_part_level: 'No options found. If you want to create one, type "Other" and select it.',
  all_configurations_done: 'All configurations in this job done',
  finish_configuration: 'Finish this configuration',
  configured_jobs: 'Configured Jobs',
  version: 'Version',
  task_completed_message: 'Task already signed off! Please re-open for additional changes!',

  create_task: {
    create_new_task: 'Create New Task',
    diagram: 'Diagram',
    instruction: 'Instruction',
    worksheet: 'Worksheet',
    task_name: 'Task Name',
    cancel: 'Cancel',
    done: 'Done',
    select_software_tool: 'Select Software Tool',
  },

  create_folder: {
    create_new_folder: 'Create New Folder',
    folder_name: 'Folder Name',
    select_software_tool: 'Select Software Tool',
    configure_new_part: 'Configure New Part',
    draw_no_b_plan: 'Draw No (B-Plan)',
    draw_no: 'Draw No',
    rotor_id: 'Rotor Id',
    parts_list: 'Parts List',
    hallmark: 'Hallmark',
    delete_title: 'DELETE',
    delete_field_confirmation: 'Delete field {label}?',
    name_prompt: 'Name',
    field_already_exists: 'Field already exists.',
    failed_create_task: 'Failed to create new task',
    start_of_stages: 'Start of stages',
    end_of_stages: 'End of stages',
  },

  emfProductList: {
    'workOrderList': 'Work Order List',
    'search': 'Search',
    'addOrder': 'Create New Project',
    'next': 'Next',
    'previous': 'Previous',

    'productLine': 'Product Line',
    'title': 'Title',
    'originalOEM': 'Original OEM',
    'frameType': 'Frame Type',
    'model': 'Model',
    'partNumber': 'Part Number',
    'customer': 'Customer',
    'projectNumber': 'Project No',
    'serialNumber': 'Serial No',
    'workOrder': 'Work Order',
    'salesForce': 'Sales Force Opportunity No',
    'createdBy': 'Created By',
  },

  loginSession: {
    'admin': 'Administrator',
    'editor': 'Editor',
    'publisher': 'Publisher',
    'inspector': 'Inspector',
    'report': 'Report',
    'dashboard': 'Dashboard',
    'language': 'Language',
    'profile': 'Profile',
    'viewProfile': 'View Profile',
    'manageFleetData': 'Manage Fleet Data',
    'reportIssue': 'Report Issue',
    'requestContent': 'Request Content',
    'contactUs': 'Contact Us',
    'training': 'Training',
    'logOut': 'Log out'
  },

  404: 'Not found',

  table_template: {
    create_table: 'Create Table',
    new_column: 'Add new column',
    new_row: 'Add new row',
    delete_column: 'Delete selected column',
    delete_row: 'Delete selected row'

  },

  calculation: {
    sum: 'Sum',
    sub: 'Subtract',
    mult: 'Multiply'
  },

  duplicate_work_order_number: 'The provided work order number is already in use',
  view_qr_code: 'View QR Code',
  download_qr_code: 'Download QR Code',
  download_traveler: 'Download Traveler',
  include_repair_assessment: 'Include Repair Assessment',
  repair_assessment: 'Repair Assessment',
  import: 'Import',
  import_editor_template: 'Existing Templates',
  import_add_excel_file: 'Add From Excel File',
  download_excel_file: 'Download Template File',
  export_task:'Export Task',
  saved: 'Saved',
  not_saved: 'Not Saved',
  level: 'Level',
  assembly: 'Assembly',
  item_number: 'Item Number',
  part_name: 'Part Name',
  part_number: 'Part Number',
  quantity: 'Quantity',
  serialNumber: 'Serial Number',
  actions: 'Actions',
  no_records: 'No Records',
  repair_assessment_empty: 'The Repair Assessment is empty. Start by importing data or adding it manually',
  repair_assessment_empty_readonly: 'The Repair Assessment is empty.',
  repair_assessment_no_templates: 'No matching templates found.',
  repair_assessment_loading_templates:
    'Loading templates, this can take a while...',
  repair_assessment_select_components: 'Select components to import',
  repair_assessment_template_load_failed: 'Could not load the template repair assessment. Please try again.',
  repair_assessment_template_empty: 'This template has no repair assessment to import.',
  close: 'Close',
  close_notification: 'Close notification',
  import_add: 'Import',
  change_log: 'Change Log',
  job_history: 'Job History',

  job_history_modal: {
    search_placeholder: 'Search all activity…',
    filter: 'Filter',
    filters: {
      all: 'All',
      overview: 'Overview',
      updates: 'Updates',
      inspector: 'Inspector',
      publisher: 'Publisher',
      report: 'Report',
    },
    compose_title: 'Post an Update',
    compose_placeholder:
      "Describe what you've done, any blockers, or updates for the team…",
    compose_submit: 'Post Update',
    compose_offline: 'Updates can only be posted while online.',
    empty_note: 'Enter an update before posting.',
  },

  change_log_modal: {
    original: 'Original',
    new: 'New',
    empty: 'No changes have been recorded yet',
    empty_filtered: 'No changes match your search or filters',
    load_more: 'Load more',
    download: 'Download',
    download_offline: 'Logs can only be downloaded while online.',
    download_truncated:
      'The download includes the first matching entries. Narrow the filters to export a smaller set.',
    search_placeholder: 'Search by name, user, or keyword',
    filter_entity_type: 'Entity type',
    filter_action: 'Action',
    filter_all_entities: 'All entities',
    filter_all_actions: 'All actions',
    clear_filters: 'Clear filters',
    filter_actions: {
      create: 'Create',
      copy: 'Copy',
      update: 'Edits',
      delete: 'Delete',
      restore: 'Restore',
      reorder: 'Reorder',
      move: 'Move',
      signOff: 'Sign off',
      statusChange: 'Status change',
      reopen: 'Reopen',
      note: 'Updates',
    },
    unknown_user: 'Unknown user',
    actions: {
      create: 'created',
      update: 'made changes to',
      delete: 'deleted',
      restore: 'restored',
      reorder: 'reordered',
      signOff: 'signed off',
      statusChange: 'changed the status',
      reopen: 'reopened',
      copy: 'copied',
      move: 'moved',
      note: 'posted an update',
    },
    phrase: {
      sub_instruction_in_task: 'a Sub-Instruction in an',
      instruction_in_task: 'an Instruction in an',
      in_the: 'in the',
      on_the: 'on the',
      the: 'the',
      of: 'of',
      from: 'from',
      findings_on_the: 'the Findings on the',
      report_revision: 'a Report Revision',
      tasks_in_project: 'tasks in the project',
      wi_in_package: 'a Work Instruction in a',
      added_part_to_the: 'added a new part to the',
      added_parts_to_the: 'added {count} new parts to the',
      added_reference_image_to_the: 'added a reference image to the',
      removed_reference_image_from_the: 'removed the reference image from the',
      editor_template: 'Editor Template',
      task_types: {
        diagram: 'Diagram Task',
        instruction: 'Instruction Task',
        table: 'Table Task',
        folder: 'Project Phase',
      },
    },
    entities: {
      job: 'Job',
      task: 'Task',
      folder: 'Folder',
      bubble: 'Bubble',
      question: 'Instruction',
      repairAssessment: 'Repair Assessment',
      workInstruction: 'Work Instruction',
      workInstructionPackage: 'Work Instruction Package',
      report: 'Report',
    },
    tools: {
      editor: 'Editor',
      publisher: 'Publisher',
      inspector: 'Inspector',
      report: 'Report',
    },
    header: {
      name: 'Name: {value}',
      assembly: 'Assembly: {value}',
      part_name: 'Part Name: {value}',
      phase: 'Phase: {value}',
      type: 'Type: {value}',
      project_phase: 'Project Phase: {value}',
      task_id: 'Task ID: {value}',
      bubble_id: 'Bubble ID: {value}',
      bubble_name: 'Bubble Name: {value}',
      bubble_type: 'Bubble Type: {value}',
      question_id: 'Question ID: {value}',
      question_name: 'Question Name: {value}',
      question_type: 'Type: {value}',
      item_number: 'Item Number: {value}',
      package_id: 'Package ID: {value}',
      major: 'Major',
      minor: 'Minor',
      rat_component: '{kind} · {assembly}',
      tasks_reordered: '{count} task(s) reordered',
      tasks_moved_to: '{count} task(s) moved to {target}',
      root_level: 'root',
      row_count: '{count} row(s)',
      bubble_count: '{count} bubble(s) created',
      question_count: '{count} instruction(s)',
      linked_tasks_count: '{count} linked task(s)',
    },
    updated: 'updated',
    fields: {
      report_note: 'Findings (By Reporter)',
      task_comment: 'Findings (By Inspector)',
      image_reference_set: 'Reference image',
    },
    status: {
      open: 'Open',
      in_progress: 'In Progress',
      signed_off: 'Signed Off',
      completed: 'Completed',
    },
  },

  add_row: 'Add Row',
  remove_row: 'Remove Row',

  repair_assessment_excel_import_failed: 'Failed to import Repair Assessment from the excel file',
  repair_assessment_add_row_failed: 'Failed to add Repair Assessment row',
  repair_assessment_update_row_failed: 'Failed to update Repair Assessment row',
  repair_assessment_unlock_failed: 'Failed to close Repair Assessment',

  reuse: 'Reuse',
  repair: 'Repair',
  replace: 'Replace',
  missing: 'Missing',
  client_reject: 'Client Reject',
  received: 'Received',
  quality_control_override: 'QC',
  reviewed: 'Eng. Review',
  comments: 'Comments',

  findings: 'Findings',
  recommendations: 'Recommendations',

  visual: 'Visual',
  dim: 'Dim',
  ndt: 'NDT',

  summary: 'Summary',
  repair_scope: 'Repair Scope',
  estimated_labor: 'Est. Hours',

  not_repair_engineer: 'Only engineers may review',

  pre_assembly_audit: 'Pre-Assembly Audit',
  donwload_rat: 'Export Components',
  repair_assessment_search_empty: 'No items available.',

  delete_all: 'Delete All',
  delete_all_message: 'This will delete all information related to the repair assessment.',
  delete_major_message: 'This will delete major components information related to the repair assessment.',
  delete_minor_message: 'This will delete minor components information related to the repair assessment.',

  repair_assessment_row_locked: 'This row is locked by {user}',

  feature_disabled: 'This feature is disabled at your location',


  locations: 'Locations',

  recycle_bin: 'Recycle Bin',
  recycle_bin_empty: 'The recycle bin is empty',

  task_name: 'Name',
  task_type: 'Type',
  deleted_by: 'Deleted By',
  deleted_at: 'Deleted At',

  executing_site: 'Executing Site',

  na: 'N/A',

  owner: 'Owner',

  mtetype_create_title: 'Create Instrument Type',
  mtetype_update_title: 'Update Instrument Type',

  create: 'Create',

  name: 'Name',
  needs_master: 'Needs Master',
  is_master: 'Is Master',

  mte_type_delete_question: 'Are you sure you want to delete this Instrument Type?',
  mte_type_delete_success: 'Instrument Type deleted successfully',
  mte_type_delete_fail: 'Failed to delete Instrument Type',

  mte_management: 'Instrumentation Log',
  add_using_camera: 'Add Using Camera',
  close_camera: 'Close Camera',
  add_manual_entry: 'Add Manual Entry',
  create_external_link:'Click to add file/link',
  select_file_to_link: 'Select a file to link to the task',

  id: 'Id',
  type: 'Type',
  due_date: 'Due Date',
  master: 'Master',
  created_at: 'Created At',

  masters: 'Masters',
  non_masters: 'Others',

  mandatory_error_message: '{field} is mandatory',
  mandatory_date_must_not_be_in_past_error_message: '{field} is mandatory and must not be in the past',
  unique_error_message: '{field} must be unique',

  task_locked_by_user: 'Task is locked by {user} until {date}',

  clear: 'clear',
  surrogate: 'Surrogate',
  stop_surrogating: 'Stop Surrogating',

  create_new_site_page:
  {
    siteName:'Site Name',
    city:'City',
    state:'State',
    country:'Country',
    create_new_site:'Create New Site',
    all_fields_required: 'Please, fill in all the required the fields.',
    site_failed: 'Site creation failed.',
    site_success: 'Site created successfully.',
  },

  edit_site_page:
  {
    edit_site: 'Edit Site',
    siteName:'Site Name',
    city:'City',
    state:'State',
    country:'Country',
    rat:'Repair Assessment Template',
    mte_m:'Instrumentation Log',
    enable_all_role_notifications: 'Enable all role-based notifications',
    enable_csm_notifications: 'CSM notifications',
    enable_repair_engineer_notifications: 'Repair Engineer notifications',
    enable_supervisor_notifications: 'Supervisor Notifications',
    enable_project_owner_notifications: 'Project owner notifications',
    enable_site_admin_notifications: 'Site Admin notifications',
    delete:'Delete',
    update:'Update',
    successMessage: 'Site updated successfully.',
    failedMessage: 'Failed to update site.',
    delete_confirm:'Are you sure you want to delete this site?',
    deletesite: 'Site deleted successfully.',
    deletefail: 'Failed to delete site.',
  },
  site_list:
  {
    siteName:'Site Name',
    city:'City',
    state:'State',
    country:'Country',
    DateModified: 'Date Modified'
  },
  announcement_banner:
  {
    title: 'Announcement Banner',
    description: 'Setup the announcement banner.',
    status: 'Status',
    message: 'Message',
    start_date: 'Start Date',
    end_date: 'End Date',
    clear_action: 'Clear',
    preview_action: 'Preview',
    save_action: 'Save',
    enabled_status: 'Enabled',
    disabled_status: 'Disabled',
    clear_question: 'This will disable the notification banner for all users. Continue?',
    end_date_error: 'End Date must be in the future'
  },
  edit_user_profile: {
    title: 'User profile',
    user_owned_template_count: 'This user is the owner of {count} Templates',
    user_owned_job_count: 'This user is the owner of {count} Jobs',
    reassign_jobs: 'Reassign Jobs',
    reassign_templates: 'Reassign Templates',
    reassign_jobs_cancel: 'Cancel Reassign',
    reassign_job: 'Reassign Job',
    confirm_reassign: 'Confirm Reassign',
    pending_approval_message: 'This user is pending approval. Check the user information and approve the profile so that the user can start using DIJ',
    rejected_message: 'This user was rejected and cannot access DIJ. The user can still be approved if needed.',
    success_message: 'User updated successfully',
    full_name: 'Full Name',
    email: 'Email',
    primary_location: 'Primary Location',
    secondary_locations: 'Secondary Locations',
    phone_number: 'Phone Number',
    role: 'Role',
    power_admin_role: 'Power Admin',
    placeholder_role: 'Role',
    ownership: 'Ownership',
    additional_permissions: 'Additional Permissions',
    enginner_review_permission: 'Engineer Review',
    delete_button: 'Delete',
    update_button: 'Update',
    approve_button: 'Approve',
    reject_button: 'Reject',
    cancel_button: 'Cancel',
    approve_question: 'Your are now approving {name}\'s user with role \'{role}\' and locations \'{locations}\'. Proceed?',
    reject_question: 'Your are now rejecting {name}\'s. The user will not be able to use DIJ. Proceed?',
    approve_success_message: 'The user has been approved successfully',
    reject_success_message: 'The user has been rejected successfully',
    update_success_message: 'The user information has been updated successfully',
    update_error_message: 'Failed to update user. You do not have permission to edit this user.',
    job_reassignment_success_message: 'The templates owned by this user have been reassigned successfully',
  },
  new_user_welcome_page: {
    welcome_configurator_message: 'Welcome to DIJ. Please select the relevant sites to your work',
    pending_approval_part1_message: 'Your request for access is now pending for approval.',
    pending_approval_part2_message: 'Please visit later for access.',
    submit: 'Submit',
  },
  user_list: {
    name: 'Full name',
    email: 'Email',
    location: 'Location',
    role: 'Role',
    last_login: 'Last Login'
  },
  users: {
    approved: 'Approved',
    approved_description: 'The list of approved users. Those users have full access to DIJ.',
    pending_approval: 'Pending Approval',
    pending_approval_description: 'The list of users to be approved. Those users cannot access DIJ until their profile is approved.',
    todays_logins: 'Today\'s Logins',
    todays_logins_description: 'The list of user that logged it today. Resets every day at midnight.',
    rejected: 'Rejected',
    rejected_description: 'The list of user that during the approval process were rejected. Those users aren\'t aware that they were reject.',
    inactive: 'Inactive',
    inactive_description: 'The list of users that do not have SEPIA information attached to their account.',
  },
  user_picker: {
    label: 'Pick a user',
    placeholder: 'Type and select available users',
    no_match_found: 'No match found',
    email: 'Email',
    role: 'Role'
  },

  sign_off_instruction_not_completed: 'Some instructions in the task have not been fully completed. Are you sure you want to sign off the task? This will lock the task from future changes.',
  sign_off_diagram_not_completed: 'Some inspection points in the task have not been completed. Are you sure you want to sign off the task? This will lock the task from future changes.',
  type_message_placeholder: 'Type your message...',

  chat_box: {
    generated_content_warning: 'Content is automatically generated by AI and it may contain innacuracy',
    type_message_placeholder: 'Type your message...',
    send: 'Send',
    regenerate: 'Regenerate',
    please_wait: 'Please wait...',
    clear_local_history: 'Clear local history',
    click_to_copy: 'Click to copy',
    returning_answer: 'Generating answer...',
    summary_generated: 'Summary generated!',
    thumbs_up: 'Helpful',
    thumbs_down: 'Not helpful',
    feedback_thanks: 'Thanks for your feedback!',
    feedback_error: 'Could not send feedback.',
    thinking: 'Thinking...',
    sources: 'Sources:',
  },
  report_template: {
    project_title: 'Project Title',
    enter_project_number: 'Enter Project Number',
    customer: 'Customer',
    enter_customer_information: 'Enter Customer Information',
    customer_tag: 'Customer Tag',
    enter_customer_tag: 'Enter Customer Tag',
    customer_po_no: 'Customer P.O. No',
    unit_serial: 'Unit Serial',
    enter_serial_info: 'Enter Serial Info',
    unit_model: 'Unit Model',
    project_number: 'Project Number',
    sales_force_opportunity_number: 'Sales Force Opportunity Number',
    enter_sales_force_opportunity_number: 'Enter Sales Force Opportunity Number',
    process: 'Process',
    enter_process: 'Enter Process',
    original_order_no: 'Original order no.',
    enter_original_order_no: 'Enter Original order no.',
    previous_orders: 'Previous Orders',
    enter_previous_orders: 'Enter Previous Orders',
    contact_person_name: 'Contact Person Name',
    enter_contact_person_name: 'Enter Contact Person Name',
    contact_person_department: 'Contact Person Department',
    executive_summary: 'Executive Summary',
    generate_executive_summary: 'Generate executive summary',
    repair_select: 'Repair Select',
    sample_text: 'Sample Text:',
    sample_text_content: 'Siemens provided technical services on behalf of Duke Energy Carolinas LLC to inspect and advise repair recommendations for an EMM32A3N rotor assembly. The rotor assembly had been sent to the Siemens repair facility in Charlotte, North Carolina for an incoming inspection.',
    revision_description: 'Revision Description',
    enter_revision_description: 'Enter Revision Description',
    csm: 'CSM',
    enter_csm: 'Enter CSM',
    repair_engineer: 'Repair Engineer',
    enter_repair_engineer: 'Enter Repair Engineer',
    major_components: 'Major Components',
    minor_components: 'Minor Components',
    work_performed_fact_finding: 'Work performed for Fact Finding Inspection',
    work_performed_repair: 'Work performed for Repair',
    repairs_not_completed_recommendations_upgrades: 'Repairs Not Completed, Recommendations & Upgrades',
    appendix: 'Appendix',
  },

  report_header: {
    internal_report: 'Internal Report',
    customer_report: 'Customer Report',
    exclude_items: 'Exclude Items',
    options: 'Options',
    progress: 'Progress',
    instrument_log: 'Instrument Log',
    documents: 'Documents',
    revisions: 'Revisions',
    description: 'Description: {description}',
    download: 'Download',
    header: 'Header',
    deviation_report: 'Deviation Report',
    repair_assessment: 'Repair Assessment',
    work_performed: 'Work performed',
    deleting_report_revision: 'Deleting report revision',
    delete_revision_confirmation: 'Are you sure you want to <b>delete</b> revision: {fileName}, description: {description}? This action cannot be undone.',
    delete_revision: 'Delete Revision',
  },

  report_tasks: {
    table: 'TABLE',
    form_inspection: 'FORM INSPECTION',
    document_number: 'Document Number:',
    revision: 'Revision:',
    no_records: 'No records',
    item: 'ITEM',
    answer: 'ANSWER',
    attachments_uc: 'ATTACHMENT(S)',
    inspector: 'INSPECTOR',
    date: 'DATE',
    inspection_diagram: 'INSPECTION DIAGRAM',
    length_measurements: 'LENGTH MEASUREMENTS',
    diameter_measurements: 'DIAMETER MEASUREMENTS',
    visual_inspection: 'VISUAL INSPECTION',
    generic_measurements: 'GENERIC MEASUREMENTS',
    label_uc: 'Label',
    part_name_uc: 'Part Name',
    description_uc: 'Description',
    question_label: 'Question:',
    name_label: 'Name:',
    description_label: 'Description:',
    expected_value_uc: 'Expected value',
    tolerance1: 'Tolerance1',
    tolerance2: 'Tolerance2',
    unit: 'Unit',
    inspector_title: 'Inspector',
    date_title: 'Date',
    condition: 'Condition',
    severity: 'Severity',
    measurement_name_uc: 'Measurement Name',
    runout_measurements: 'RUNOUT MEASUREMENTS',
    tir: 'TIR',
    tolerance: 'Tolerance',
    task_prefix: 'Task:',
    show_oot_colors: 'Show out of tolerance colors',
    free_finding: 'FREE FINDING',
    attachments_plain: 'ATTACHMENTS',
    pictures: 'pictures',
    excluded: 'excluded',
    no_images: 'no images',
  },

  deviation_report: {
    unit_serial: 'Unit Serial:',
    review_only: 'Review only',
    action_required: 'Action required',
    assembly_bom: 'Assembly BOM',
    assembly_ref: 'Assembly REF',
    item_number: 'Item #',
    original_pn: 'Original PN',
    actions: 'Actions',
  },

  external_data: {
    title: 'EXTERNAL DATA',
    upload_failed: 'File upload failed',
    item_uc: 'ITEM',
    file_name: 'File Name',
    print_to_report: 'Print to report',
    file_type_error: 'This file type can not be printed to report',
    delete: 'Delete',
    too_many_attachments: 'Too many attachments. Maximum allowed is {max} attachments.',
    upload_successful: 'Attachment upload successful.',
  },

  viewer_3d: {
    downloading_file: 'Downloading file...',
    loading_file: 'Loading file...',
    screenshot: 'Screenshot',
    select_task_screenshot: 'Select a task to save the screenshot:',
  },

  findings: {
    title: 'FINDINGS',
    by_inspector: 'By Inspector',
    by_reporter: 'By Reporter',
    placeholder: 'Please enter your findings here...',
  },

  close_message: 'Close message',
  close_banner: 'Close banner',
  other: 'Other',
  enter_an_option: 'Enter an option',
  tap_to_start_writing: 'Tap me to start writing',
  breadcrumbs: 'Breadcrumbs',    
  close: 'Close',

  diagram_builder: {
    repair_icon: 'Repair Icon',
    bubble_processing: 'Bubble still being processed',
    import_diagram: 'Import a diagram',
    choose_files: 'Choose files',
    note_runout_points_missing: 'Note: The diagram does not contain all of the runout points found in the file.',
    note_points_missing: 'Note: The diagram does not contain all of the points found in the file.',
    laserscan_import_success: 'LaserScan values imported successfully.',
    not_all_dij_points_filled: 'Note: not all DIJ points were filled.',
    all_inspection_values_imported_successfully: 'All inspection values imported successfully.',
    image_size: 'Image Size',
    edit_comment: 'Edit Comment',
    comment: 'Comment',
    your_comment: 'Your comment',
    fine: 'Fine',
    major_component: 'Major Component',
    minor_component: 'Minor Component',
    label: 'Label',
    expected: 'Exp',
    tolerance: 'Tol',
    empty_diagram: 'Empty Diagram',
    take_photo: 'Take photo',
    tir: 'TIR',
    position_image: 'Position Image',
    description_options: {
      other: 'Other',
      te_bearing_journal: 'TE bearing journal',
      nte_bearing_journal: 'NTE bearing journal',
      te_seal_fit: 'TE seal fit',
      nte_seal_fit: 'NTE seal fit',
      coupling_fit: 'Coupling fit',
      probe_track: 'Probe track',
      bridgeover: 'Bridgeover',
      thrust_disc_fit: 'Thrust disc fit',
      balance_piston: 'Balance piston',
      overspeed_trip_fit: 'Overspeed trip fit',
      te_gland: 'TE gland',
      nte_gland: 'NTE gland',
      '1st_stg_impeller_eye': '1st stg impeller eye',
      '2nd_stg_impeller_eye': '2nd stg impeller eye',
      '3rd_stg_impeller_eye': '3rd stg impeller eye',
      '4th_stg_impeller_eye': '4th stg impeller eye',
      '5th_stg_impeller_eye': '5th stg impeller eye',
      '6th_stg_impeller_eye': '6th stg impeller eye',
      '7th_stg_impeller_eye': '7th stg impeller eye',
      '8th_stg_impeller_eye': '8th stg impeller eye',
      '1st_2nd_stg_spacer': '1st-2nd stg spacer',
      '2nd_3rd_stg_spacer': '2nd-3rd stg spacer',
      '3rd_4th_stg_spacer': '3rd-4th stg spacer',
      '4th_5th_stg_spacer': '4th-5th stg spacer',
      '5th_6th_stg_spacer': '5th-6th stg spacer',
      '6th_7th_stg_spacer': '6th-7th stg spacer',
      '7th_8th_stg_spacer': '7th-8th stg spacer',
      journal_area_te: 'Journal area TE',
      journal_area_nte: 'Journal area NTE',
      thrust_collar_fit: 'Thrust collar fit',
      center_balance_plane: 'Center balance plane',
      rh_cplg_tpr_fit: 'RH CPLG TPR Fit',
      lh_cplg_tpr_fit: 'LH CPLG TPR Fit',
      extension_fit_te: 'Extension fit TE',
      extension_fit_nte: 'Extension fit NTE',
      exciter_fit: 'Exciter fit',
      fan_fit_te: 'Fan fit TE',
      fan_fit_nte: 'Fan fit NTE',
      dummy_piston_fit: 'Dummy piston fit',
      oil_deflector_fit_te: 'Oil deflector fit TE',
      oil_deflector_fit_nte: 'Oil deflector fit NTE',
      hydrogen_seal_fit_te: 'Hydrogen seal fit TE',
      hydrogen_seal_fit_nte: 'Hydrogen seal fit NTE',
      collector_ring_fit_te: 'Collector ring fit TE',
      collector_ring_fit_nte: 'Collector ring fit NTE',
      te_brg_seal_area: 'TE Brg seal area',
      nte_brg_seal_area: 'NTE Brg seal area',
      thrust_runner_fit: 'Thrust runner fit',
    },
  },

  admin_page: {
    create_new_role: 'Create New Role',
    new_role_name: 'New Role Name',
    select_software_tools: 'Select Software Tools',
    edit_role: 'Edit Role',
    delete: 'Delete',
    update: 'Update',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    features: "Features",
    role: "Role",
    roles: "Roles",
    role_name: "Role Name",
    add_role: "Add Role",
    add_site: "Add Site",
    add_mte_type: "Add Instrument Type",
    needs_master: "Needs Master",
    is_master: "Is Master",
    filter: "Filter",
    search: "Search...",
    all: "All",
    fixes: "Fixes",
    removals: "Removals",
    permitted_tools: "Permitted Tools",
    date_modified: "Date Modified",
    user: "User",
    sites: "Sites",
    mte_types: "Instrument Types",
    instrumentation: "Instrumentation",
    add_instrumentation: "Add Instrumentation",
    notification_banner: "Notification Banner",
    notification_templates: "Notification Templates",
    notification_templates_description: "Create reusable templates for publisher notifications.",
    add_notification_template: "Add Notification Template",
    edit_notification_template: "Edit Notification Template",
    template_name: "Template Name",
    template_type: "Type",
    template_locale: "Locale",
    template_subject: "Subject Template",
    template_body: "Body Template",
    template_status: "Status",
    template_active: "Active",
    manual_notification_template: "Manual notification template",
    template_default: "Default",
    template_placeholders_help: "Available placeholders",
    notification_event_settings_title: "Notification event settings",
    notification_event_settings_description: "Choose which template should be sent for each notification event.",
    notification_event_settings_loading: "Loading events...",
    notification_event_settings_enabled: "Enabled",
    notification_event_settings_archived_warning: "The assigned template is archived. Please choose another one.",
    template_required_fields: "Name, subject and body are required",
    template_saved_successfully: "Template saved successfully",
    archive_notification_template_confirm: "Archive this notification template?",
    template_archived_successfully: "Template archived successfully",
    actions: "Actions",
    active: "Active",
    archived: "Archived",
    archive: "Archive",
    next: "Next",
    back: "Back",
    create_role: "Create Role",
    default_role_error: "Sorry, you cannot change default roles.",
    success_default: "Action completed successfully.",
    failed_default: "Action failed. Please try again.",
    project_phases: 'Project Phases',
    add_project_phases: 'Add Project Phases'
  },

  conflict: {
    title: 'Version Conflicts Detected',
    yours: 'Yours',
    theirs: 'Theirs',
    done: 'Done',
    confirm: 'Confirm',
    cancel: 'Cancel',
    use_mine_all: 'Use Mine for All',
    use_theirs_all: 'Use Theirs for All',
    img_reference: 'Image Reference',
    keep_mine: 'Keep Mine',
    use_theirs: 'Use Theirs',
    unknown: 'Unknown',
    last_changed_by: 'Last changed by:',
  },

  instruction_view: {
    create_new: 'Create new',
    create_sub_instruction: 'Create new sub-instruction',
    table_image_alt: 'Table image',
    image_alt: 'Image',
    add_option: 'Add an option',
    add_other_option: 'Add "Other" option',
    manage_options: 'Manage Options',
    use_image_reference: 'Use image as task reference',
    upload_image: 'Upload image',
    upload_image_alt: 'Table image',
    uploading: 'Uploading...',
    choose_one_item: 'Choose one item',
    take_photo: 'Take photo',
    choose_file: 'Choose file',
    question: 'Question',
    import: 'Import',
    table: 'table',
    rows: 'Rows',
    columns: 'Columns',
    csv_import_note: 'Note: The headings of the columns should be on the first line of the CSV file. The import only works for CSV file, not Excel file.',
    action_option: 'Action',
    yes_no_option: 'Yes/No',
    multiple_choice_option: 'Multiple Choice',
    single_choice_option: 'Single Choice',
    text_number_option: 'Text',
    table_option: 'Table',
    smart_table_option: 'Smart Table',
    import_csv_option: 'Import CSV Table',
    done: 'Done',
  },

  shared: {
    loading: 'Loading...',
    no_records: 'No records',
    submit: 'Submit',
    finish: 'Finish',
    select_everything: 'Select Everything',
    deselect_everything: 'Deselect Everything',
    warning: 'Warning',
    tasks: 'Tasks',
    task: 'Task',
    all: 'All',
    status: 'Status',
    signed_off_by: 'Signed Off By',
    signed_off_at: 'Signed Off At',
    percent_complete: '% Complete',
    tool_bar: 'Tool Bar',
    id: 'ID',
    major_components: 'Major Components',
    minor_components: 'Minor Components',
    generated_repair_recommendations: 'Generate repair recommendations for this part',
    summarize_repair_recommendations: 'Summarize repair recommendations',
    shop_notes: 'Shop Notes',
    serial_number: 'Serial Number',
    helpers: 'Helpers',
    saved: 'Saved',
    close: 'Close',
    documents: 'Documents',
    upload_attachments: 'Upload Attachments',
    download: 'Download',
    upload: 'Upload',
    leadman_sign_off_required: 'Leadman sign off required',
    require_leadman_signing: 'Require leadman signing',
    chat_with_rs: 'Chat with Repair Select',
    status_complete: 'Complete',
    status_open: 'Open',
  },

  version_page: {
    dij_releases: "DIJ Releases",
    features: "Features",
    fixes: "Fixes",
    removals: "Removals",
    improvements: "Improvements",
    hotfix: "Hotfix",
    updates: "Updates",
    version: "Version",
    date: "Date",
    tag: "Tag"
  },
  common: {
    close: "Close",
    save: "Save",
    uploading_progress: "Uploading {uploaded}/{total}"
  },
  version_form: {
    title: "Add New Version",
    version: "Version",
    date: "Date",
    date_placeholder: "Click to select a date",
    section: "Section",
    section_placeholder: "Select a section",
    details: "Details",
    tag: "Tag"
  },
  file_explorer: {
    select_all: 'Select all',
    recycle_bin: 'Recycle Bin',
    image_gallery: 'Image Gallery',
    exclude_images: 'Exclude Images',
    include_images: 'Include Images',
    attach_images: 'Attach Images',
    restore_file: 'Restore file',
    root: 'Root (/)'
  },

  notifications: 'Notifications',
  notifications_modal: {
    section_selected: 'Selected section',
    watchers: 'Watchers',
    csm: 'Client Service Manager (CSM)',
    repair_engineer: 'Repair Engineer',
    send_notification: 'Send Notification',
    notification_log: 'Notification Log',
    loading_log: 'Loading notification log...',
    no_notification_log: 'No notifications sent yet',
    from: 'From',
    to: 'To',
    sent_at: 'Sent',
    template: 'Template',
    no_template: 'No template (manual message)',
    default_template: 'default',
    send: 'Send',
    add_watchers: 'Add Watchers for this Project',
    selected_watchers: 'Selected Watchers',
    no_watchers: 'No watchers added yet',
    notify_users: 'Notify Users',
    role: 'Role',
    search_roles: 'Search roles...',
    site_label: 'Site',
    role_users_for_site: 'Users in selected role for this job site',
    no_role_users_for_site: 'No users found for this role at this site',
    csm: 'CSM',
    repair_engineer: 'Repair Engineer',
    supervisor: 'Supervisor(s)',
    project_owner: 'Project Owner',
    not_assigned: 'Not Assigned',
    other_users: 'Other Users',
    select_users: 'Select Additional Users',
    note: 'Note',
    notification_message: 'Notification Message',
    template_preview: 'Template Preview',
    no_template_preview: 'Select a template to preview it here',
    recipients: 'Recipients',
    exclude_recipient: 'Exclude recipient',
    add_recipient: 'Add Recipient',
    auto_included_from_site_setting: 'This recipient will be automatically included based on the site\'s notification settings',
    notification_disabled_from_site_setting: 'This recipient is disabled based on the site\'s notification settings',
    search_users: 'Search users...',
    add_me_as_watcher: 'Add me as watcher',
    remove_me_from_watchers: 'Remove me from watchers',
    active_watchers: 'Active watchers',
    available_placeholders: 'Available placeholders',
    auto_short: 'auto',
    disabled_short: 'disabled',
    notification_history: 'Notification history',
    search_notifications: 'Search notifications...',
    no_matching_notifications: 'No matching notifications found.',
    items_count: '{filtered} / {total} item(s)',
    remove: 'Remove',
    watchers_updated: 'Watchers updated successfully',
    notification_sent: 'Notification sent successfully'
  },
  sign_off_diagram_not_completed: 'Some inspection points in the task have not been completed. Are you sure you want to sign off the task? This will lock the task from future changes.',

  work_instructions_contains: '{count} work instruction(s) available',
  work_instructions_empty: 'No work instructions for this job',
  add_work_instruction: 'New Work Instruction Package',
  create_work_instruction: 'Create Work Instruction',
  new_work_instruction_package: 'New Work Instruction Package',
  select_work_instruction_package: 'Select Work Instruction Package',
  work_instruction_title: 'Title',
  charge_code: 'Charge Code',
  disabled_wi_signoff:'All linked tasks must be signed off before the work instruction can be signed off.'
};

export default en;
